"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { X, Check } from "lucide-react";

type CellVal = boolean;
type Row = {
  feature: { en: string; es: string };
  offline: CellVal;
  online: CellVal;
};

// Honest list: what keeps working when the connection drops vs what waits for it.
const ROWS: Row[] = [
  { feature: { en: "Ring cash sales (saved on the device)", es: "Cobrar en efectivo (se guarda en el dispositivo)" }, offline: true, online: true },
  { feature: { en: "Card payments", es: "Pagos con tarjeta" }, offline: false, online: true },
  { feature: { en: "Open, close or add to tabs", es: "Abrir, cerrar o sumar a tabs" }, offline: false, online: true },
  { feature: { en: "Split checks", es: "Dividir cuentas" }, offline: false, online: true },
  { feature: { en: "Online orders", es: "Pedidos online" }, offline: false, online: true },
  { feature: { en: "KDS shows the tickets it already has", es: "El KDS muestra los tickets que ya tenía" }, offline: true, online: true },
  { feature: { en: "New tickets reach the KDS", es: "Tickets nuevos llegan al KDS" }, offline: false, online: true },
];

function Cell({ value }: { value: CellVal }) {
  if (value === true) {
    return (
      <div className="flex items-center justify-center">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center"
          style={{ backgroundColor: "rgba(46,204,113,0.15)" }}
        >
          <Check className="w-4 h-4" style={{ color: "#2ECC71" }} strokeWidth={3} />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center">
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center"
        style={{ backgroundColor: "rgba(220,38,38,0.12)" }}
      >
        <X className="w-4 h-4" style={{ color: "#DC2626" }} strokeWidth={3} />
      </div>
    </div>
  );
}

export default function OfflineCompareScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("What works offline", "Qué funciona offline")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Cash keeps going. ", "El efectivo sigue. ")}
            <span className="text-redy-charcoal/30">{t("The rest waits for WiFi.", "Lo demás espera al WiFi.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Pull the WiFi at 7 PM and here's exactly what keeps working. Cash sales keep ringing and are saved on the device. Cards, tabs, splits and online orders need a connection — they pick back up the moment it returns.",
              "Apaga el WiFi a las 7 PM y esto es exactamente lo que sigue funcionando. Las ventas en efectivo siguen y se guardan en el dispositivo. Tarjeta, tabs, cuentas divididas y pedidos online necesitan conexión — vuelven en cuanto regresa."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-[#FAFAF8] rounded-[24px] border border-black/5 overflow-hidden max-w-[860px] mx-auto"
        >
          {/* Mobile scroll hint */}
          <div className="md:hidden text-center text-[11px] font-bold text-redy-secondary/60 uppercase tracking-wider py-2 bg-white border-b border-black/5">
            {t("← Scroll to see more →", "← Desliza para ver más →")}
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[560px]">
              {/* Header */}
              <div className="grid grid-cols-4 bg-redy-charcoal px-6 py-4">
                <div className="col-span-2 text-[12px] font-bold text-white/55">{t("Capability", "Capacidad")}</div>
                <div className="col-span-1 text-[12px] font-black text-white text-center">{t("WiFi down", "Sin WiFi")}</div>
                <div className="col-span-1 text-[12px] font-bold text-white/55 text-center">{t("WiFi back", "Con WiFi")}</div>
              </div>

              {/* Rows */}
              <div className="divide-y divide-black/5 bg-white">
                {ROWS.map((row, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.4, delay: 0.1 + 0.05 * i }}
                    className="grid grid-cols-4 px-6 py-4 items-center"
                  >
                    <div className="col-span-2 text-[13px] font-bold text-redy-charcoal">
                      {t(row.feature.en, row.feature.es)}
                    </div>
                    <Cell value={row.offline} />
                    <Cell value={row.online} />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
